import React, { useEffect, Fragment } from "react"
import PropTypes from "prop-types"
import DeleteSite from "./DeleteSite"
import CmsIntegrations from "@modules/site/settings/components/CmsIntegrations"
import { PageWithTabsContentSection } from "@modules/ui/layouts/Containers"
import { useTracker } from "@modules/analytics"
import {
  SidebarColumn,
  Content,
  InnerLayout,
} from "@modules/ui/layouts/ColumnLayout"
import { useSiteSettingsNavigation } from "./Settings.helpers"
import { SiteSettingsSection } from "../constants"
import GeneralSettingsSection from "./GeneralSection"
import ReportsSection from "./ReportsSection"
import { isProduction } from "../../../env/constants"
import ExperimentalFeaturesSection from "./ExperimentalFeaturesSection"
import BuildsSection from "./BuildsSection"
import { IntegrationTargetSection } from "./IntegrationTargetSection"
import { useSitePermissionsFieldsFragment } from "@modules/site/shared/fragments.generated"
import { PreviewSettingsSection } from "./PreviewSettingsSection"

import { useQuery } from "@apollo/react-hooks"
import gql from "graphql-tag"

const propTypes = {
  siteId: PropTypes.string.isRequired,
  buildsEnabled: PropTypes.bool,
  organizationId: PropTypes.string,
  orgName: PropTypes.string,
  sitePreviewPassword: PropTypes.string,
  sitePreviewProtection: PropTypes.string,
  sitePreviewDirectory: PropTypes.string,
  productionBranch: PropTypes.string,
  siteDescription: PropTypes.string,
  location: PropTypes.object,
}

function SiteSettings({
  buildsEnabled,
  siteId,
  siteName,
  organizationId,
  orgName,
  buildsTier,
  hostingTier,
  sitePreviewPassword,
  sitePreviewProtection,
  sitePreviewDirectory,
  productionBranch,
  siteDescription,
  repository,
  location,
  sitePrefix,
}) {
  const { trackPageViewed } = useTracker()
  useEffect(() => {
    trackPageViewed(`Site Settings`)
  }, [])

  const { data: { permissions } = {} } = useSitePermissionsFieldsFragment(
    siteId
  )

  const { sections, sidebarNavOptions } = useSiteSettingsNavigation({
    organizationId,
    siteId,
    location,
    buildsEnabled,
  })

  /**
   * The following code can be removed once we launch the content loader to all users
   * We are both currently hiding it behind a feature flag as well as only showing it to WordPress users
   */
  const [showContentSyncInfo, setShowContentSyncInfo] = React.useState(false)
  const { data, error } = useQuery(
    gql`
      query gatsbyDependenciesForSite($siteId: UUID!) {
        gatsbyDependenciesForSite(siteId: $siteId) {
          name
          installedVersion
        }
      }
    `,
    {
      variables: { siteId },
    }
  )

  React.useEffect(() => {
    const supportedPlugin = !!data?.gatsbyDependenciesForSite.find(dep => {
      return [`gatsby-source-wordpress`, `gatsby-source-contentful`].includes(
        dep.name
      )
    })
    setShowContentSyncInfo(supportedPlugin && !error)
  }, [data, error])

  sidebarNavOptions[0].subItems = sidebarNavOptions[0]?.subItems.filter(
    subItem => {
      if (subItem.name === `content-sync`) {
        return showContentSyncInfo
      } else {
        return true
      }
    }
  )

  return (
    <PageWithTabsContentSection>
      <InnerLayout>
        <SidebarColumn options={sidebarNavOptions} />
        <Content>
          {sections.map(({ name, active, subsections }, i) => {
            if (!active) {
              return null
            }
            /**
             * The following code can be removed once we launch the content loader to all users
             * We are both currently hiding it behind a feature flag as well as only showing it to WordPress users
             */
            const filteredSubsections = subsections?.filter(({ name }) => {
              if (name === SiteSettingsSection.ContentLoader) {
                return showContentSyncInfo
              } else {
                return true
              }
            })
            let settingsSection = null
            switch (name) {
              case SiteSettingsSection.Builds:
                settingsSection = (
                  <BuildsSection
                    siteId={siteId}
                    buildsTier={buildsTier}
                    hostingTier={hostingTier}
                    organizationId={organizationId}
                  />
                )
                break
              case SiteSettingsSection.Preview:
                settingsSection = <PreviewSettingsSection siteId={siteId} />
                break
              case SiteSettingsSection.ExperimentalFeatures:
                settingsSection = (
                  <ExperimentalFeaturesSection siteId={siteId} />
                )
                break
              case SiteSettingsSection.Reports:
                settingsSection = (
                  <ReportsSection
                    siteId={siteId}
                    siteName={siteName}
                    buildsEnabled={buildsEnabled}
                    permissions={permissions}
                  />
                )
                break
              case SiteSettingsSection.General:
                settingsSection = (
                  <GeneralSettingsSection
                    sections={filteredSubsections}
                    organizationId={organizationId}
                    siteId={siteId}
                    siteName={siteName}
                    sitePrefix={sitePrefix}
                    productionBranch={productionBranch}
                    sitePreviewDirectory={sitePreviewDirectory}
                    sitePreviewProtection={sitePreviewProtection}
                    sitePreviewPassword={sitePreviewPassword}
                    siteDescription={siteDescription}
                    repository={repository}
                    buildsEnabled={buildsEnabled}
                    permissions={permissions}
                  />
                )
                break
              case SiteSettingsSection.Danger:
                settingsSection = (
                  <DeleteSite
                    siteId={siteId}
                    siteName={siteName}
                    permissionToDelete={permissions?.sites?.delete}
                  />
                )
                break
              case SiteSettingsSection.DataSources:
                settingsSection = (
                  <CmsIntegrations
                    siteId={siteId}
                    orgSiteName={`${orgName}/${siteName}`}
                    permissionToEdit={permissions?.sites?.edit}
                  />
                )
                break
              case SiteSettingsSection.Hosting:
                settingsSection = (
                  <IntegrationTargetSection
                    siteId={siteId}
                    siteName={siteName}
                  />
                )
                break
              default:
                if (!isProduction) {
                  console.error(`Unexpected section found: ${name} - ${i}`)
                }
                settingsSection = null
            }

            return <Fragment key={name}>{settingsSection}</Fragment>
          })}
        </Content>
      </InnerLayout>
    </PageWithTabsContentSection>
  )
}

SiteSettings.propTypes = propTypes

export default SiteSettings
